// @ts-check

/**
 * @file Cross-component bridge that lets a screen flag itself as loading
 * without each screen re-implementing a full-screen loading branch.
 *
 * `useScreenLoading(loading)` is called from a screen's `setup()`; while
 * the boolean is true the layout shows a generic loading indicator over
 * the content area. Multiple screens may briefly overlap on transitions,
 * so the active count is tracked instead of a single boolean.
 */

import {useEffect, useSyncExternalStore} from "react"

let activeLoadingCount = 0
/** @type {Set<() => void>} */
const subscribers = new Set()

/**
 * Notifies every subscriber of the current loading state.
 * @returns {void} Nothing.
 */
function notifySubscribers() {
  for (const subscriber of subscribers) {
    subscriber()
  }
}

/**
 * Returns the current aggregate loading state.
 * @returns {boolean} Whether any screen has flagged itself loading.
 */
function screenLoadingSnapshot() {
  return activeLoadingCount > 0
}

/**
 * Subscribes a layout component to loading state changes.
 * @param {() => void} subscriber Callback triggered when loading state changes.
 * @returns {() => void} Unsubscribe callback.
 */
function subscribeToScreenLoading(subscriber) {
  subscribers.add(subscriber)
  return () => {
    subscribers.delete(subscriber)
  }
}

/**
 * Screen-side hook: while `loading` is true the layout shows its loading
 * indicator. Cleanup runs when the screen unmounts or when `loading`
 * flips to false.
 * @param {boolean} loading Whether the calling screen is currently loading.
 * @returns {void} Nothing.
 */
export function useScreenLoading(loading) {
  useEffect(() => {
    if (!loading) return undefined
    activeLoadingCount += 1
    notifySubscribers()
    return () => {
      activeLoadingCount -= 1
      notifySubscribers()
    }
  }, [loading])
}

/**
 * Layout-side hook: returns the aggregate loading state and re-renders
 * when any screen toggles its loading flag.
 * @returns {boolean} Whether any screen has flagged itself loading.
 */
export function useAggregateScreenLoading() {
  return useSyncExternalStore(subscribeToScreenLoading, screenLoadingSnapshot, screenLoadingSnapshot)
}
